import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { PromotionPreset } from "../types";
import {
  deletePreset,
  loadPresets,
  persistPresets,
  renamePreset,
  updatePresetMfes,
} from "../preset-utils";

import "@bee/pattern-library";

@customElement("preset-manager")
export class PresetManager extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .preset-list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-8, 8px);
    }

    bee-card::part(body) {
      padding: var(--spacing-12, 12px) var(--spacing-16, 16px);
    }

    bee-card::part(base) {
      transition: box-shadow 0.15s ease;
    }

    bee-card:hover::part(base) {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .preset-item {
      display: flex;
      align-items: center;
      gap: var(--spacing-12, 12px);
      font-size: 13px;
    }

    .preset-info {
      flex: 1;
      min-width: 0;
    }

    .preset-name {
      font-weight: 500;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .preset-meta {
      font-size: 11px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin-top: 2px;
      display: flex;
      gap: var(--spacing-4, 4px);
      flex-wrap: wrap;
    }

    .preset-actions {
      display: flex;
      gap: 4px;
      flex-shrink: 0;
    }

    .empty {
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      text-align: center;
      font-size: 13px;
      padding: var(--spacing-12, 12px);
    }

    .edit-mfe-list {
      max-height: 400px;
      overflow-y: auto;
    }

    .edit-mfe-row {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      padding: var(--spacing-4, 4px) 0;
      font-size: 13px;
    }

    .rename-form {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      margin-bottom: var(--spacing-8, 8px);
    }
  `;

  /** All known MFE names (from environment data), used to populate edit checkboxes */
  @property({ type: Array }) availableMfes: string[] = [];
  @property({ type: Boolean }) showEmptyState = true;

  @state() private _presets: PromotionPreset[] = [];
  @state() private _editingPreset: PromotionPreset | null = null;
  @state() private _editMfes: Set<string> = new Set();
  @state() private _renamingId: string | null = null;
  @state() private _renameValue = "";

  connectedCallback() {
    super.connectedCallback();
    this._presets = loadPresets();
    this._notifyPresetCountChanged();
  }

  private _persist() {
    persistPresets(this._presets);
    this._notifyPresetCountChanged();
  }

  private _notifyPresetCountChanged() {
    this.dispatchEvent(new CustomEvent("preset-count-changed", {
      detail: { count: this._presets.length },
      bubbles: true,
      composed: true,
    }));
  }

  private _handleDelete(id: string) {
    this._presets = deletePreset(this._presets, id);
    this._persist();
  }

  private _startRename(preset: PromotionPreset) {
    this._renamingId = preset.id;
    this._renameValue = preset.name;
  }

  private _confirmRename() {
    if (!this._renamingId || !this._renameValue.trim()) return;
    this._presets = renamePreset(this._presets, this._renamingId, this._renameValue.trim());
    this._persist();
    this._renamingId = null;
    this._renameValue = "";
  }

  private _cancelRename() {
    this._renamingId = null;
    this._renameValue = "";
  }

  private _startEdit(preset: PromotionPreset) {
    this._editingPreset = preset;
    this._editMfes = new Set(preset.mfes);
  }

  private _toggleEditMfe(mfe: string) {
    const next = new Set(this._editMfes);
    if (next.has(mfe)) {
      next.delete(mfe);
    } else {
      next.add(mfe);
    }
    this._editMfes = next;
  }

  private _confirmEdit() {
    if (!this._editingPreset) return;
    this._presets = updatePresetMfes(this._presets, this._editingPreset.id, [...this._editMfes]);
    this._persist();
    this._editingPreset = null;
    this._editMfes = new Set();
  }

  private _cancelEdit() {
    this._editingPreset = null;
    this._editMfes = new Set();
  }

  private _formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  render() {
    return html`
      <div class="preset-list">
        ${this._presets.length === 0
          ? this.showEmptyState
            ? html`<div class="empty">No presets saved. Select MFEs on the Versions page and use "Save Selection as Preset" to create one.</div>`
            : nothing
          : this._presets.map((p) => this._renderPreset(p))}
      </div>
      ${this._editingPreset ? this._renderEditModal() : nothing}
    `;
  }

  private _renderPreset(preset: PromotionPreset) {
    if (this._renamingId === preset.id) {
      return html`
        <bee-card>
          <div class="preset-item">
            <div class="rename-form">
              <bee-input
                size="small"
                .value=${this._renameValue}
                @bee-input=${(e: CustomEvent) => { this._renameValue = (e.target as HTMLInputElement).value; }}
                @keydown=${(e: KeyboardEvent) => { if (e.key === "Enter") this._confirmRename(); if (e.key === "Escape") this._cancelRename(); }}
              ></bee-input>
              <bee-button label="Save" bee-type="primary" size="small" @click=${() => this._confirmRename()}></bee-button>
              <bee-button label="Cancel" bee-type="secondary-grey" size="small" @click=${() => this._cancelRename()}></bee-button>
            </div>
          </div>
        </bee-card>
      `;
    }

    return html`
      <bee-card>
        <div class="preset-item">
          <div class="preset-info">
            <div class="preset-name">${preset.name}</div>
            <div class="preset-meta">
              <bee-tag color="brand" small>${preset.mfes.length} MFEs</bee-tag>
              <bee-tag color="gray" small>${this._formatDate(preset.createdAt)}</bee-tag>
            </div>
          </div>
          <div class="preset-actions">
            <bee-button label="Edit MFEs" bee-type="secondary-grey" size="small" @click=${() => this._startEdit(preset)}></bee-button>
            <bee-button label="Rename" bee-type="secondary-grey" size="small" @click=${() => this._startRename(preset)}></bee-button>
            <bee-button label="Delete" bee-type="destructive-tertiary" size="small" @click=${() => this._handleDelete(preset.id)}></bee-button>
          </div>
        </div>
      </bee-card>
    `;
  }

  private _renderEditModal() {
    const preset = this._editingPreset!;
    const mfes = this.availableMfes.length > 0 ? this.availableMfes : preset.mfes;

    return html`
      <bee-modal
        open
        in-root
        title="Edit Preset MFEs"
        @bee-request-close=${() => this._cancelEdit()}
      >
        <div style="margin-bottom:12px;font-size:13px;color:var(--colors-foreground-fg-gray-700,#5a6072);">
          ${preset.name} — select MFEs to include in this preset
        </div>
        <div class="edit-mfe-list">
          ${[...mfes].sort().map((mfe) => html`
            <div class="edit-mfe-row">
              <bee-checkbox
                .checked=${this._editMfes.has(mfe)}
                size="small"
                @bee-change=${() => this._toggleEditMfe(mfe)}
              ></bee-checkbox>
              <span>${mfe}</span>
            </div>
          `)}
        </div>
        <div slot="footer" style="display:flex;gap:8px;justify-content:flex-end;width:100%">
          <bee-button label="Cancel" bee-type="secondary-grey" @click=${() => this._cancelEdit()}></bee-button>
          <bee-button
            label="Save Changes"
            bee-type="primary"
            ?disabled=${this._editMfes.size === 0}
            @click=${() => this._confirmEdit()}
          ></bee-button>
        </div>
      </bee-modal>
    `;
  }
}
